import { ProfilePayload, ProfileWizard } from "@earned/wizard";
import { READ_ONLY_PROFILE_WIZARD_FIELDS } from "@modules/tasks/constants";
import { useUpdateProfileInfo } from "@modules/tasks/mutations";
import { useProfileInfo } from "@modules/tasks/queries";
import { useProfileWizardStore, useTasksStore } from "@modules/tasks/state";
import { TaskTemplateId, WizardId } from "@modules/tasks/types";
import * as React from "react";
import _ from "lodash";
import { useWizardCloseConfirm } from "@modules/tasks/hooks.web";

interface ProfileWizardControllerProps {
  clients: Forme.ClientUser[];
}

const ProfileWizardController: React.FC<ProfileWizardControllerProps> = ({
  clients,
}) => {
  const {
    wizardId,
    setWizardId,
    isWizardOpen,
    setIsWizardOpen,
    wizardTask,
    setWizardTask,
    setRemoteCompleteTaskId,
  } = useTasksStore();
  const primaryClient = React.useMemo(
    () => clients.find((client) => client.isPrimary) as Forme.ClientUser,
    [clients]
  );
  const coClient = React.useMemo(
    () => clients.find((client) => !client.isPrimary),
    [clients]
  );
  const isCoClient = React.useMemo(
    () =>
      wizardTask?.templateId ===
      TaskTemplateId.UPDATE_PERSONAL_INFORMATION_COCLIENT,
    [wizardTask?.templateId]
  );
  const { profileInfo, isLoading, isRefetching } = useProfileInfo();
  const {
    fields: cache,
    defaultPage,
    setDefaultPage,
    setField,
  } = useProfileWizardStore();
  const {
    mutateAsync,
    isLoading: isSubmitting,
    isSuccess,
    reset,
  } = useUpdateProfileInfo();

  const isOpen = React.useMemo(
    () => wizardId === WizardId.PROFILE && isWizardOpen,
    [wizardId, isWizardOpen]
  );

  const { openCloseConfirmDialog, CloseConfirmDialogComponent } =
    useWizardCloseConfirm({
      onConfirmClose: reset,
    });

  const handleSubmit = React.useCallback(
    async (payload: ProfilePayload) => {
      if (!profileInfo) return;
      // Strip read-only fields from client that are not collected in the form
      const strippedClient = _.omit(
        payload.client,
        READ_ONLY_PROFILE_WIZARD_FIELDS
      );
      // Only send the client being edited:
      const mergedPayload: ProfilePayload = isCoClient
        ? { coClient: payload.coClient }
        : { client: strippedClient as ProfilePayload["client"] };
      await mutateAsync({ data: mergedPayload });
    },
    [mutateAsync, isCoClient, profileInfo]
  );

  const handleDone = React.useCallback(async () => {
    if (wizardTask) setRemoteCompleteTaskId(wizardTask.id);
    setIsWizardOpen(false);
    setWizardTask(null);
    setWizardId(null);
    reset();
  }, [
    reset,
    setWizardId,
    setWizardTask,
    setIsWizardOpen,
    setRemoteCompleteTaskId,
    wizardTask,
  ]);

  const initialFields = React.useMemo(() => {
    const enrichedProfileInfo = {
      ...profileInfo,
      client: {
        ...profileInfo?.client,
        FIRST_NAME:
          profileInfo?.client?.FIRST_NAME || primaryClient.firstName || "",
        LAST_NAME:
          profileInfo?.client?.LAST_NAME || primaryClient.lastName || "",
        EMAIL: profileInfo?.client?.EMAIL || primaryClient.email || "",
      },
    };
    return _.merge({}, enrichedProfileInfo, cache);
    /** ignoring this to intentionally not rerender when the cache is updated:  */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileInfo]);
  return (
    <>
      <ProfileWizard
        isOpen={isOpen}
        close={openCloseConfirmDialog}
        primaryClientName={`${primaryClient.firstName} ${primaryClient.lastName}`}
        coClientName={
          coClient ? `${coClient.firstName} ${coClient.lastName}` : undefined
        }
        isCoClient={isCoClient}
        onSubmit={handleSubmit}
        isSuccess={isSuccess}
        isSubmitting={isSubmitting}
        isLoading={(isLoading || isRefetching) && !profileInfo}
        onDone={handleDone}
        cache={initialFields}
        onFieldChange={(config) =>
          setField({ key: config.key, value: config.field })
        }
        initialPage={defaultPage}
        onPageChange={setDefaultPage}
        modalRootSelector="#wizard-root"
      />
      <CloseConfirmDialogComponent />
    </>
  );
};

export default ProfileWizardController;
